import React, { useCallback, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import styles from "./Style.module.css";
import { Typography, Paper } from "@mui/material";
import NoDataFound from "../NoDataFound/NoDataFound";
import { FileDownloadOutlined } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import capitalizeFirstLetter from "../../../../hooks/CommonFunction";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const cityName = payload[0]?.payload?.city?.name || "N/A";
    const enrollment = payload[0]?.payload?.enrollment || 0;
    const doctor = payload[0]?.payload?.doctors ?? "-";
    return (
      <Paper
        elevation={3}
        style={{
          padding: "10px",
          backgroundColor: "#fff",
          border: "1px solid #ccc",
        }}
      >
        <Typography variant="subtitle2">
          <strong>Doctor:</strong> {doctor}
        </Typography>
        <Typography variant="body2">
          <strong>Enrollment:</strong> {enrollment}
        </Typography>
        <Typography variant="subtitle2">
          <strong>City:</strong> {capitalizeFirstLetter(cityName)}
        </Typography>
      </Paper>
    );
  }

  return null;
};

const CityEnrollmentChart = ({
  data = [],
  isImgDownload = false,
  isExpand = false,
  onExpand,
  height = 250,
  isExport,
  handleExport,
  downloadTitle="",
  colors
}) => {
  const chartRef = useRef();
  const barGroupWidth = 110; // Width per bar group
  const chartWidth = data?.length * barGroupWidth;
  const minChartWidth = 600;

  const NO_SCROLL_MAX = 15;
  const isScrollable = (data?.length || 0) > NO_SCROLL_MAX;

  const GROUP_WIDTH = 80;
  const scrollWidth = (data?.length || 0) * GROUP_WIDTH;

  const handleDownload = useCallback(() => {
    if (!chartRef.current) return;

    const svg = chartRef.current.container?.firstChild;
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const paddingTop = 40;
      const canvas = document.createElement("canvas");
      canvas.width = svg.clientWidth || 1200;
      canvas.height = (svg.clientHeight || 600) + paddingTop;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#636578";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("City Wise Enrollment", 10, 10);

      ctx.drawImage(img, 0, paddingTop);

      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `City Wise Enrollment.png`;
      a.click();
    };
    img.src = url;
  }, []);

    const CustomLegend = ({ colors }) => {
    return (
      <div className={styles.legendContainer}>
        <div className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: "#1565C0" }}
          ></span>
          {"City Name"}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <div className={styles.header}>
          <div className={styles.dFlex}>
            <Typography
              variant="h5"
              className="heading"
            >
              City Wise Enrollment
            </Typography>
            {isExpand && (
              <OpenInNewIcon
                onClick={onExpand}
                color="primary"
                size="small"
                className={styles.expandIcon}
              />
            )}
            {isImgDownload && (
              <FileDownloadOutlined
                onClick={handleDownload}
                color="primary"
                size="small"
                className={styles.expandIcon}
              />
            )}
          </div>

          <div>
            {isExport && (
              <div className={styles.dFlex2}>
                <Typography variant="h5" className="heading">
                  {downloadTitle}
                </Typography>
                <FileDownloadOutlined
                  onClick={handleExport}
                  color="primary"
                  size="small"
                  className={styles.expandIcon}
                />
              </div>
            )}
          </div>
        </div>
         <div className={styles.dFlex1}>
          <CustomLegend colors={colors} />
        </div>
        {data?.length > 0 ? (
          <div className={styles.scrollable}>
            <div
              style={{
                overflowX: isScrollable ? "auto" : "hidden",
                overflowY: "hidden",
              }}
            >
              <div style={{ width: isScrollable ? scrollWidth : "100%" }}>
                <ResponsiveContainer width="100%" height={height}>
                  <BarChart
                    // height={500}
                    data={data}
                    ref={chartRef}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    barGap={8}
                    barCategoryGap={isScrollable ? "20%" : "30%"}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="city.name"
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tickFormatter={(value) =>
                        value.charAt(0).toUpperCase() + value.slice(1)
                      }
                    />
                    <YAxis />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "transparent" }}
                    />
                    <Bar dataKey="enrollment" fill="#1565C0" barSize={30}>
                      <LabelList
                        dataKey="enrollment"
                        position="top"
                        style={{
                          fill: "#161616",
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.noData}>
            <NoDataFound />
          </div>
        )}
      </div>
    </div>
  );
};

export default CityEnrollmentChart;
